import { el, clear, createSheet, toast, type SheetCtl } from "./components";
import type { Store, Direction } from "../store";
import { CATEGORIES, LEVELS, type Category, type Level } from "../deck";
import { logout, flushSync } from "../sync";

let sheet: SheetCtl | null = null;

export interface SettingsCallbacks {
  onChange: () => void;
  onReset: () => void;
}

export function openSettings(store: Store, cb: SettingsCallbacks): void {
  if (!sheet) sheet = createSheet("settings", "Einstellungen");
  render(store, cb);
  sheet.open();
}

function field(labelText: string, helpText: string, control: HTMLElement): HTMLElement {
  return el("div", { class: "field" }, [
    el("label", { text: labelText }),
    el("div", { class: "help", text: helpText }),
    control,
  ]);
}

function render(store: Store, cb: SettingsCallbacks): void {
  if (!sheet) return;
  clear(sheet.body);
  const s = store.state.settings;

  const dirSelect = el("select") as HTMLSelectElement;
  const dirOpts: Array<[Direction, string]> = [
    ["hr-de", "Kroatisch → Deutsch"],
    ["de-hr", "Deutsch → Kroatisch"],
    ["mix", "Gemischt"],
  ];
  for (const [val, label] of dirOpts) {
    const o = document.createElement("option");
    o.value = val;
    o.textContent = label;
    if (val === s.dir) o.selected = true;
    dirSelect.appendChild(o);
  }
  dirSelect.addEventListener("change", () => {
    store.state.settings.dir = dirSelect.value as Direction;
    store.save();
    cb.onChange();
  });

  const audioToggle = el("button", {
    class: `switch ${s.audio ? "on" : ""}`,
    attrs: { "aria-pressed": String(s.audio), "aria-label": "Audio umschalten" },
    on: {
      click: () => {
        store.state.settings.audio = !store.state.settings.audio;
        audioToggle.classList.toggle("on", store.state.settings.audio);
        audioToggle.setAttribute("aria-pressed", String(store.state.settings.audio));
        store.save();
        cb.onChange();
      },
    },
  });

  const audioRow = el("div", { class: "switch-row" }, [
    el("div", {}, [
      el("label", { text: "Audio" }),
      el("div", { class: "help", text: "Kroatische Aussprache per Web Speech." }),
    ]),
    audioToggle,
  ]);

  const newPerDaySelect = el("select") as HTMLSelectElement;
  const newOpts: Array<[number | string, string]> = [
    [10, "10"],
    [15, "15"],
    [20, "20"],
    [30, "30"],
    [9999, "Alle"],
  ];
  for (const [val, label] of newOpts) {
    const o = document.createElement("option");
    o.value = String(val);
    o.textContent = label;
    if (Number(val) === s.newPerDay) o.selected = true;
    newPerDaySelect.appendChild(o);
  }
  newPerDaySelect.addEventListener("change", () => {
    store.state.settings.newPerDay = Number(newPerDaySelect.value);
    store.save();
    cb.onChange();
  });

  const delaySelect = el("select") as HTMLSelectElement;
  const delayOpts: Array<[number, string]> = [
    [1000, "1 Sek"],
    [2000, "2 Sek"],
    [3000, "3 Sek"],
    [5000, "5 Sek"],
    [8000, "8 Sek"],
  ];
  for (const [val, label] of delayOpts) {
    const o = document.createElement("option");
    o.value = String(val);
    o.textContent = label;
    if (val === s.autoAdvanceMs) o.selected = true;
    delaySelect.appendChild(o);
  }
  delaySelect.addEventListener("change", () => {
    store.state.settings.autoAdvanceMs = Number(delaySelect.value);
    store.save();
    cb.onChange();
  });

  const levelChips = el("div", { class: "chip-row" });
  const activeLevels = new Set<Level>(s.allowedLevels);
  for (const lv of LEVELS) {
    const chip = el("button", {
      class: `chip ${activeLevels.has(lv) ? "on" : ""}`,
      text: lv,
      on: {
        click: () => {
          const isOn = chip.classList.toggle("on");
          if (isOn) {
            if (!store.state.settings.allowedLevels.includes(lv)) {
              store.state.settings.allowedLevels.push(lv);
            }
          } else {
            const remaining = store.state.settings.allowedLevels.filter(l => l !== lv);
            if (remaining.length === 0) {
              chip.classList.add("on");
              toast("Mindestens ein Niveau aktiv");
              return;
            }
            store.state.settings.allowedLevels = remaining;
          }
          store.save();
          cb.onChange();
        },
      },
    });
    levelChips.appendChild(chip);
  }

  const catChips = el("div", { class: "chip-row" });
  const disabled = new Set(s.disabledCats);
  for (const cat of CATEGORIES) {
    const chip = el("button", {
      class: `chip ${disabled.has(cat) ? "" : "on"}`,
      text: cat,
      on: {
        click: () => {
          const on = chip.classList.toggle("on");
          if (on) {
            store.state.settings.disabledCats = store.state.settings.disabledCats.filter(
              c => c !== cat
            );
          } else {
            if (!store.state.settings.disabledCats.includes(cat)) {
              store.state.settings.disabledCats.push(cat as Category);
            }
          }
          store.save();
          cb.onChange();
        },
      },
    });
    catChips.appendChild(chip);
  }

  const exportBtn = el("button", {
    class: "btn btn-ghost",
    text: "Export JSON",
    on: {
      click: () => {
        const blob = new Blob([store.export()], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "hr-a1-quiz.json";
        a.click();
        URL.revokeObjectURL(url);
        toast("Exportiert");
      },
    },
  });

  const importInput = el("input", {
    attrs: { type: "file", accept: "application/json" },
    style: { display: "none" },
  }) as HTMLInputElement;
  importInput.addEventListener("change", () => {
    const f = importInput.files?.[0];
    if (!f) return;
    f.text().then(text => {
      if (store.import(text)) {
        toast("Importiert");
        cb.onChange();
        render(store, cb);
      } else {
        toast("Import fehlgeschlagen");
      }
    });
  });

  const importBtn = el("button", {
    class: "btn btn-ghost",
    text: "Import JSON",
    on: { click: () => importInput.click() },
  });

  const logoutBtn = el("button", {
    class: "btn btn-ghost",
    text: "Abmelden",
    on: {
      click: async () => {
        await flushSync();
        await logout();
        location.reload();
      },
    },
  });

  const resetBtn = el("button", {
    class: "btn btn-danger danger",
    text: "Alles zurücksetzen",
    on: {
      click: () => {
        if (confirm("Wirklich allen Fortschritt löschen? Dies kann nicht rückgängig gemacht werden.")) {
          store.reset();
          toast("Zurückgesetzt");
          cb.onReset();
          render(store, cb);
        }
      },
    },
  });

  sheet.body.appendChild(
    field("Niveau", "Welche CEFR-Stufen mitlernen? Karten höherer Stufen sind anspruchsvoller.", levelChips)
  );
  sheet.body.appendChild(field("Abfragerichtung", "Wie sollen Karten abgefragt werden?", dirSelect));
  sheet.body.appendChild(audioRow);
  sheet.body.appendChild(
    field("Weiter-Verzögerung", "Nach der Antwort automatisch weiter. Tippen überspringt die Wartezeit.", delaySelect)
  );
  sheet.body.appendChild(
    field("Neue Karten pro Tag", "Pro Modus: so viele neue Karten täglich einführen.", newPerDaySelect)
  );
  sheet.body.appendChild(
    field("Kategorien", "Klick zum De-/Aktivieren.", catChips)
  );
  sheet.body.appendChild(
    el("div", { class: "sheet-actions" }, [exportBtn, importBtn, importInput, logoutBtn, resetBtn])
  );
}
